<template>
  <div
    class="flex flex-col items-center justify-center w-full bg-white h-full rounded-lg overflow-hidden mb-16 md:w-[400px] m-auto"
  >
    <div class="mt-3">
      {{ currentLocation.name }}
    </div>
    <div class="italic text-gray-400 text-center px-5">
      {{ currentLocation.address }}
    </div>
    <div class="text-2xl font-bold mt-2 mb-8">
      {{ $t("appointments.title") }}
    </div>
    <div v-if="!selectedService" class="w-full px-4">
      <div
        v-for="service in services"
        :key="service.id"
        class="p-4 flex flex-row items-center justify-between -mx-3 border-t-[1px] border-gray-300 py-6"
      >
        <div>
          <div>
            {{ service.name }}
          </div>
          <div class="text-gray-400 text-xs w-[90%]">
            {{ service.description }}
          </div>
        </div>
        <div class="flex flex-row items-center justify-between">
          <div class="flex flex-col mr-4">
            <div class="font-bold">
              {{ $filters.currency(service.price) }}
            </div>
            <div class="text-xs">{{ service.durationMinutes }} min</div>
          </div>
          <div>
            <n-button
              type="primary"
              size="medium"
              @click="handleServiceSelected(service)"
              >{{ $t("appointments.book") }}</n-button
            >
          </div>
        </div>
      </div>
    </div>
    <div v-else class="flex flex-col items-center justify-center w-full">
      <div
        v-if="showEmployeeSelector"
        class="w-full border-b border-gray-200 pb-5 mb-8"
      >
        <div
          class="w-screen md:w-full flex flex-row overflow-x-auto px-5 space-x-5 whitespace-nowrap"
        >
          <div
            v-for="employee in employeeSelectorOptions"
            :key="employee.id || 'anyone'"
            class="flex flex-col items-center min-w-[72px] cursor-pointer"
            @click="handleEmployeeSelected(employee.id)"
          >
            <div
              class="relative h-16 w-16 rounded-full border-2 bg-gray-100 flex flex-row items-center justify-center overflow-visible"
              :class="{
                'border-accent': employee.id === selectedEmployeeId,
                'border-gray-200': employee.id !== selectedEmployeeId,
              }"
            >
              <img
                v-if="employee.photoImageId"
                class="h-full w-full rounded-full object-cover"
                :src="employeePhotoUrl(employee)"
                :alt="employee.name"
              />
              <c-icon
                v-else
                name="PersonFilled"
                size="2.4rem"
                class="text-gray-300"
              />
              <div
                v-if="employee.id === selectedEmployeeId"
                class="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-accent text-contrast border-2 border-white flex flex-row items-center justify-center"
              >
                <c-icon name="CheckRound" size="0.9rem" />
              </div>
            </div>
            <div
              class="mt-2 max-w-[86px] truncate text-sm"
              :class="{
                'text-blue font-semibold': employee.id === selectedEmployeeId,
                'text-gray-400': employee.id !== selectedEmployeeId,
              }"
            >
              {{ employee.name }}
            </div>
          </div>
        </div>
      </div>
      <div class="mb-12 scale-125">
        <n-date-picker
          v-model:value="selectedDay"
          panel
          :is-date-disabled="startAvailabilityDateIsDisabled"
          size="large"
          :first-day-of-week="0"
          :actions="[]"
        >
          <template #prev-year>
            <div />
          </template>
          <template #prev-month>
            <div class="absolute mt-36 -ml-12 font-bold text-gray-400">
              <c-icon name="ArrowBackIosRound" size="1rem" />
            </div>
          </template>
          <template #next-month>
            <div class="absolute mt-36 ml-12 font-bold text-gray-400">
              <c-icon name="ArrowForwardIosRound" size="1rem" />
            </div>
          </template>
          <template #next-year> <div /></template>
        </n-date-picker>
      </div>
      <div class="w-full h-[1px] bg-gray-200" />
      <div
        v-if="selectedDayAvailableSlots.length > 0"
        id="slots-container"
        class="pb-1 w-screen md:w-full mt-5 flex flex-row md:grid md:grid-cols-4 md:gap-4 snap-x overflow-x-auto px-12 md:px-5 whitespace-nowrap"
      >
        <div
          v-for="(slot, index) in selectedDayAvailableSlots"
          :key="index"
          :id="`${slot}`"
          class="flex flex-row items-center text-center mr-2 md:mr-0 justify-center px-6 py-2 cursor-pointer shadow rounded-3xl border-gray-300"
          :class="{
            'text-white': selectedSlot === slot,
          }"
          :style="{ backgroundColor: selectedSlot === slot ? buttonColor : '' }"
          @click="selectedSlot = slot"
        >
          {{ slot }}
        </div>
      </div>
      <div v-else class="mt-6 font-bold">
        {{ $t("appointments.no-slots") }}
      </div>
      <div class="bg-gray-200 mt-6 w-11/12 rounded-lg px-4 py-3">
        <div class="flex flex-row justify-between items-start text-base">
          <div class="font-bold">
            {{ selectedService.name }}
          </div>
          <div class="flex flex-col items-end">
            <div class="font-bold">
              {{ $filters.currency(selectedService.price) }}
            </div>
            <div class="text-xs">
              {{ selectedServiceDurationSlot }}
            </div>
          </div>
        </div>
      </div>
      <div class="mt-5 w-11/12">
        <div class="text-xs mb-1">
          {{ $t("appointments.comment") }}
        </div>
        <n-input
          v-model:value="comment"
          :placeholder="$t('appointments.comment-placeholder')"
        />
      </div>

      <div class="w-full bg-white py-6 px-6">
        <div class="text-xs mb-3">
          {{ $t("appointments.email-confirmation") }}
        </div>
        <div class="flex flex-row items-center justify-between">
          <div class="w-1/3">
            <div class="font-bold text-lg">
              {{ $filters.currency(selectedService.price) }}
            </div>
            <div class="text-xs">{{ selectedService.durationMinutes }} min</div>
          </div>
          <div class="">
            <div
              :disabled="!selectedSlot"
              class="w-[200px] font-bold text-lg text-center py-2 text-white rounded-lg"
              :style="{ backgroundColor: buttonColor }"
              @click="bookAppointment"
            >
              {{ $t("appointments.book") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api.js";
import { mapState, mapActions } from "pinia";
import { authStore } from "../stores/auth";
import Moment from "moment";
import { extendMoment } from "moment-range";
const moment = extendMoment(Moment);
import CIcon from "../../../core-ui/src/components/CIcon.vue";
import googleConversion from "@/googleConversion.js";
import utils from "@comeback/core/src/utils.js";

export default {
  name: "AppointmentPage",
  data() {
    return {
      services: [],
      employees: [],
      selectedEmployeeId: null,
      selectedService: null,
      currentAppointments: [],
      schedule: [],
      selectedDay: new Date().getTime(),
      selectedSlot: null,
      comment: "",
    };
  },
  async mounted() {
    googleConversion.parseAndStoreToken();
    let that = this;
    let response = await api.get(
      `/appointments/organization/${this.organization.id}/services`
    );
    this.services = response.data;
    response = await api.get(
      `/appointments/organization/${this.organization.id}/employees`
    );
    this.employees = response.data;
    response = await api.get(
      `/appointments/location/${this.currentLocation.id}/appointments`
    );
    this.currentAppointments = response.data;
    response = await api.get(
      `/appointments/location/${this.currentLocation.id}/schedule`
    );
    this.schedule = response.data;

    window.addEventListener("popstate", () => {
      if (!that.$route.query.serviceSelected) {
        that.selectedService = null;
      }
    });
  },
  computed: {
    ...mapState(authStore, [
      "organization",
      "currentLocation",
      "currentCustomer",
      "branding",
    ]),
    showEmployeeSelector() {
      return this.employees.length >= 2;
    },
    employeeSelectorOptions() {
      return [
        {
          id: null,
          name: this.$t("appointments.anyone"),
          photoImageId: null,
        },
        ...this.employees,
      ];
    },
    buttonColor() {
      return utils.colorIsDark(this.branding.accentColor)
        ? this.branding.accentColor
        : this.branding.backgroundColor;
    },
    appointmentEmployeeCandidates() {
      if (this.selectedEmployeeId) {
        return this.employees.filter(
          (employee) => employee.id === this.selectedEmployeeId
        );
      }
      return this.employees.length > 0 ? this.employees : [null];
    },
    selectedServiceDurationSlot() {
      if (!this.selectedSlot) return "";
      return `${this.selectedSlot} - ${moment(this.selectedSlot, "HH:mm")
        .add(this.selectedService.durationMinutes, "minutes")
        .format("HH:mm")}`;
    },
    servicesById() {
      return this.services?.reduce((acc, service) => {
        acc[service.id] = service;
        return acc;
      }, {});
    },
    selectedDayAvailableSlots() {
      if (!this.selectedService) return [];
      return this.getSlots(this.selectedDay);
    },
  },
  methods: {
    ...mapActions(authStore, ["updateAppointment"]),
    async bookAppointment() {
      let employeeId = this.getAvailableEmployeeIdForSlot(this.selectedSlot);
      if (this.employees.length > 0 && !employeeId) return;
      let startTime = moment(this.selectedDay)
        .set({
          hours: this.selectedSlot.split(":")[0],
          minutes: this.selectedSlot.split(":")[1],
          seconds: "00",
        })
        .toDate();
      let appointment = {
        serviceId: this.selectedService.id,
        startTime: startTime,
        comment: this.comment,
        employeeId,
      };
      this.updateAppointment(appointment);
      if (this.currentCustomer?.id) {
        await api.post(
          `/appointments/location/${this.currentLocation.id}/appointment`,
          { ...appointment, customerId: this.currentCustomer.id }
        );
        googleConversion.sendConversionEvent();
        this.$router.push({
          name: "downloadPage",
          params: {
            ...this.$route.params,
          },
        });
      } else {
        this.$router.push({
          name: "createCustomerPage",
          params: {
            ...this.$route.params,
          },
        });
      }
    },
    slotIsInWorkingHours(slot, daySchedule) {
      let workingRanges = daySchedule.map((schedule) => {
        let [startHours, startMinutes] = schedule.startTime
          .split(":")
          .map(Number);
        let [endHours, endMinutes] = schedule.endTime.split(":").map(Number);
        return moment.range(
          moment(slot).set({ hours: startHours, minutes: startMinutes }),
          moment(slot)
            .set({ hours: endHours, minutes: endMinutes })
            .subtract(this.selectedService.durationMinutes, "minutes")
        );
      });
      return workingRanges.some((range) => range.contains(moment(slot)));
    },
    getSlots(day) {
      let slots = [];
      let slotMinutes = 15;
      let daySchedule = this.schedule[moment(day).weekday()];
      if (!daySchedule) return [];
      let appointmentsFromDay = this.currentAppointments.filter((appointment) =>
        moment(appointment.startTime).isSame(moment(day), "day")
      );
      let time = moment(day).set({
        hours: 0,
        minutes: 0,
        seconds: 0,
      });
      for (let i = 0; i < 100; i++) {
        if (
          !this.slotFilled({
            appointmentsFromDay,
            daySchedule,
            slot: time,
          })
        ) {
          slots.push(time.format("HH:mm"));
        }
        time.add(slotMinutes, "minutes");
      }
      return slots;
    },
    handleEmployeeSelected(employeeId) {
      this.selectedEmployeeId = employeeId;
      this.updateSelectedSlot();
    },
    updateSelectedSlot() {
      let availableSlots = this.selectedDayAvailableSlots;
      if (!availableSlots.includes(this.selectedSlot)) {
        this.selectedSlot = availableSlots[0] || null;
      }
    },
    employeePhotoUrl(employee) {
      return `https://res.cloudinary.com/comebackapp/image/upload/f_auto,q_auto,w_128,h_128,c_fill/${employee.photoImageId}`;
    },
    handleServiceSelected(service) {
      this.selectedService = service;
      this.selectedSlot = this.selectedDayAvailableSlots[0] || null;
      this.$router.push({
        name: "appointmentPage",
        params: {
          ...this.$route.params,
        },
        query: { ...this.$route.query, serviceSelected: true },
      });
    },
    startAvailabilityDateIsDisabled(timestamp) {
      let today = moment().startOf("day").toDate();
      return timestamp < today;
    },
    getAvailableEmployeeIdForSlot(slot) {
      if (!slot) return null;
      let appointmentsFromDay = this.currentAppointments.filter((appointment) =>
        moment(appointment.startTime).isSame(moment(this.selectedDay), "day")
      );
      let daySchedule = this.schedule[moment(this.selectedDay).weekday()];
      let slotMoment = moment(this.selectedDay).set({
        hours: Number(slot.split(":")[0]),
        minutes: Number(slot.split(":")[1]),
        seconds: 0,
      });
      let employee = this.availableEmployeesForSlot({
        appointmentsFromDay,
        daySchedule,
        slot: slotMoment,
      })[0];
      return employee?.id || null;
    },
    availableEmployeesForSlot({ appointmentsFromDay, daySchedule, slot }) {
      return this.appointmentEmployeeCandidates.filter((employee) =>
        this.employeeIsAvailableForSlot({
          appointmentsFromDay,
          daySchedule,
          employeeId: employee?.id || null,
          slot,
        })
      );
    },
    employeeIsAvailableForSlot({
      appointmentsFromDay,
      daySchedule,
      employeeId,
      slot,
    }) {
      if (!this.slotIsInWorkingHours(slot, daySchedule)) {
        return false;
      }
      return !appointmentsFromDay.some((appointment) => {
        let appointmentDurationMinutes =
          this.servicesById[appointment.serviceId]?.durationMinutes ||
          appointment.durationMinutes ||
          this.selectedService.durationMinutes;
        let range1 = moment.range(
          moment(appointment.startTime),
          moment(appointment.startTime).add(
            appointmentDurationMinutes,
            "minutes"
          )
        );
        let range2 = moment.range(
          moment(slot),
          moment(slot).add(this.selectedService.durationMinutes, "minutes")
        );
        if (!range1.overlaps(range2)) return false;
        return (
          !employeeId ||
          !appointment.employeeId ||
          appointment.employeeId === employeeId
        );
      });
    },
    slotFilled({ appointmentsFromDay, daySchedule, slot }) {
      return (
        this.availableEmployeesForSlot({
          appointmentsFromDay,
          daySchedule,
          slot,
        }).length === 0
      );
    },
  },
  components: {
    CIcon,
  },
  watch: {
    selectedDay() {
      this.updateSelectedSlot();
    },
    employees() {
      this.updateSelectedSlot();
    },
  },
};
</script>
